/**
 * 平台「尺码组」嵌入口（P5.5-5 · 2026-10-12）。
 *
 * 主人裁定：平台菜单点开后看到的**就是分销那一套**尺码组界面（D9：同一管理功能的
 * 界面只维护一套）。未开通分销的商家由平台在分销侧惰性开一个 `MD_ONLY` 租户。
 *
 * **与既有 `#ext=` 外链面板的唯一区别是「跨域」**，所以不能复用 `showExtPanel`：
 * 那里的 `normalizeExtHref` 只取 `pathname + search`，会把
 * `https://dist.ducai.cc/admin/...` 削成 `/admin/...` —— iframe 当场指向**本平台
 * 自己**的同名路径（404，或更糟：打开一个无关页面而没人发现）。
 *
 * 地址不由前端拼：向平台 `GET /merchant/dist-md-portal` 要一次，后端负责
 * 惰性建租户 + 按**当前会话**现签短票，返回**绝对** `iframe_url`。
 * 前端只干两件事：塞进 iframe，把各段失败摊成看得懂的一条提示（不白屏、不抛错）。
 *
 * 缓存策略：**同一个人、同一地址、上次确实加载成功**时不再取票也不重载 ——
 * 短票只有 5 分钟，但 iframe 里的分销会话已经建立，重载反而把用户踢回登录页。
 * 键含 `person_id`：登出会清掉它、换人也会变（见 `identityKey`），
 * 所以「同商家换个人」不会接着用上一个人的分销会话，换商家更是当场作废。
 */
import { api } from "../api.js";
import { state } from "../state.js";
import {
  emitRoute,
  extFrame,
  markNavActive,
  moduleTitleFromNav,
  revealExtShell,
  setExtNotice,
  setPageTitle,
} from "./ext-shell.js";

/** NAV 项上的 `route`；与后端 `NAV_ITEMS` 里「尺码组」那一项逐字一致。 */
export const DIST_MD_ROUTE = "md-sizegroups";
/** 承载标记：NAV 项 `embedPanel` 取此值即由本模块承载（不是 `external`）。 */
export const DIST_MD_EMBED = "dist-md";

const ENDPOINT = "/api/v1/merchant/dist-md-portal";
const LS_MERCHANT = "fit1_mc_merchant_id";
const LS_PERSON = "fit1_mc_person_id";

/** 后端 `enabled:false` 的 `reason` → 用户看得懂的话。未知 reason 走兜底。 */
const REASON_TEXT = {
  no_portal_url: "分销地址未配置，请联系平台管理员。",
  no_person: "请用微信扫码登录控制台后再打开尺码组（接口密钥登录换不了票）。",
  tenant_unavailable: "尺码组暂不可用，请稍后重试。",
};

let current = { key: "", url: "" };

/** 会话身份：商家 + 人。任一变了，上一张票与 iframe 里的会话都不该接着用。 */
function identityKey() {
  const mid = (state.me && state.me.merchant_id) || localStorage.getItem(LS_MERCHANT) || "";
  const pid =
    (state.me && state.me.console_person_id) || localStorage.getItem(LS_PERSON) || "";
  return `${mid}|${pid}`;
}

function assign(frame, url) {
  if (!frame || !url) return;
  // 跨域地址**直赋**，绝不 normalize（见文件头）。
  if (frame.getAttribute("src") === url) return;
  frame.setAttribute("src", url);
}

export async function showDistMdPanel() {
  revealExtShell();
  const link = document.querySelector(`#side-nav [data-route="${DIST_MD_ROUTE}"]`);
  markNavActive(link);
  setPageTitle(moduleTitleFromNav(link) || "尺码组");
  emitRoute("__ext");

  const frame = extFrame();
  if (!frame) return;
  const key = identityKey();

  // 已经在看的就是这一页：不取票、不重载（重载会换掉 iframe 里已建立的会话）
  if (
    current.url &&
    current.key === key &&
    frame.dataset.mdLoaded === "1" &&
    frame.getAttribute("src") === current.url
  ) {
    setExtNotice("");
    return;
  }

  setExtNotice("正在打开尺码组…");
  let data = null;
  let ok = false;
  try {
    const res = await api("GET", ENDPOINT);
    ok = !!res.ok;
    data = res.data;
  } catch {
    ok = false;
  }
  if (!ok || !data) {
    setExtNotice("尺码组加载失败，请稍后重试。");
    return;
  }
  if (!data.enabled || !data.iframe_url) {
    // 换过身份就先把上一家的页面清掉，别让它继续留在屏幕上
    if (current.key && current.key !== key) frame.setAttribute("src", "about:blank");
    setExtNotice(REASON_TEXT[data.reason] || "尺码组暂不可用，请稍后重试。");
    return;
  }
  const url = data.iframe_url;
  if (current.key && current.key !== key) frame.setAttribute("src", "about:blank");
  setExtNotice("");
  frame.dataset.mdLoaded = "";
  // 只认**本次**这条地址加载完成：`load` 属性会被后续的外链面板复用，靠 src 比对挡住
  frame.addEventListener(
    "load",
    () => {
      if (frame.getAttribute("src") === url) {
        current = { key, url };
        frame.dataset.mdLoaded = "1";
      }
    },
    { once: true }
  );
  assign(frame, url);
}

/* ── 平台导入页 → 编排详情（2026-10-20）────────────────────────────────────

   主子报「点『编排详情』出现『已打开分销编排详情』，界面没窗口」查实的缺口：
   采集助手把导入页开在**本控制台**的 `#ext=` iframe 里（`drama-garments.html`），
   导入页 `postMessage` 给 `window.parent`，而控制台**根本没有监听** —— 消息投进空气，
   子页却当成功。分销台那边有监听（`pages/md.js`），平台这边没有。

   这里补上监听：仍然**换一次票**把分销台嵌进面板（与「尺码组」同一口子），
   只是落点带 `?layout=<款号>`，由分销 `app.js` 的 `openLayoutFromHash()` 直接
   弹出那一款的编排层。**绝不自己拼 iframe 地址**（跨域 + 短票，见文件头）。 */

const LAYOUT_NEXT_PREFIX = "/admin/?embed=1#/md/spu?layout=";

const LAYOUT_REASON_TEXT = {
  no_portal_url: "分销地址未配置，请联系平台管理员。",
  no_person: "请用微信扫码登录控制台后再打开编排详情（接口密钥登录换不了票）。",
  tenant_unavailable: "编排详情暂不可用，请稍后重试。",
};

/**
 * 打开某一款的「编排详情」（分销那一套，嵌进右侧面板）。
 *
 * @param {string} spuCode 平台款号（导入页回执里的 `spu_code`）
 * @param {{ beforeAssign?: () => void }} [hooks] `beforeAssign` 在 iframe 跳转**前**
 *        回调 —— 导入页要的回执必须赶在跳转前发，跳完那个窗口就没了。
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
export async function openDistSpuLayout(spuCode, hooks) {
  const code = String(spuCode || "").trim();
  if (!code) {
    return { ok: false, message: "导入回执缺少款号，请到分销「主数据 → 货盘」打开编排详情" };
  }
  const frame = extFrame();
  if (!frame) return { ok: false, message: "控制台缺少外链面板，请刷新本页重试" };

  revealExtShell();
  markNavActive(null);
  setPageTitle("编排详情");
  emitRoute("__ext");
  setExtNotice("正在打开编排详情…");

  let data = null;
  try {
    const res = await api(
      "GET",
      ENDPOINT + "?next=" + encodeURIComponent(LAYOUT_NEXT_PREFIX + encodeURIComponent(code))
    );
    data = res && res.ok ? res.data : null;
  } catch {
    data = null;
  }
  if (!data) {
    setExtNotice("编排详情加载失败，请稍后重试。");
    return { ok: false, message: "编排详情加载失败，请稍后重试" };
  }
  if (!data.enabled || !data.iframe_url) {
    const msg = LAYOUT_REASON_TEXT[data.reason] || "编排详情暂不可用，请稍后重试。";
    setExtNotice(msg);
    return { ok: false, message: msg };
  }

  setExtNotice("编排详情已打开；点左侧「服装」可回到导入页。");
  if (hooks && typeof hooks.beforeAssign === "function") {
    try {
      hooks.beforeAssign();
    } catch {
      /* 回执发不出去不影响打开 */
    }
  }
  current = { key: identityKey(), url: data.iframe_url };
  frame.dataset.mdLoaded = "";
  assign(frame, data.iframe_url);
  return { ok: true };
}

/** 只认控制台自己那个外链 iframe 发来的消息（`ev.source` 比对，不靠 origin 猜）。 */
function onImportIframeMessage(ev) {
  const d = ev && ev.data;
  if (!d || d.type !== "ducai-fit-detail-layout" || d.action !== "open") return;
  const frame = extFrame();
  if (!frame || ev.source !== frame.contentWindow) return;

  const reqId = String(d.req_id || "").trim();
  const ack = (ok, message) => {
    if (!reqId || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        { type: "ducai-fit-detail-layout-ack", req_id: reqId, ok: !!ok, message: message || "" },
        ev.origin || window.location.origin
      );
    } catch {
      /* iframe 已在跳转 */
    }
  };

  const code = String(d.spu_code || d.product_id || d.num_iid || "").trim();
  openDistSpuLayout(code, { beforeAssign: () => ack(true) })
    .then((r) => {
      if (!r || !r.ok) ack(false, (r && r.message) || "编排详情打不开，请稍后重试");
    })
    .catch((e) => ack(false, (e && e.message) || "编排详情打不开，请稍后重试"));
}

if (typeof window !== "undefined" && !window.__ducaiDistLayoutBridge) {
  window.__ducaiDistLayoutBridge = true;
  window.addEventListener("message", onImportIframeMessage);
}
